import { Controller } from "@hotwired/stimulus"
import 'vanilla-cookieconsent/dist/cookieconsent.js'
import { truncateExceptionMessages } from "../utils/truncate_exception_messages"

const pendingTrialLeads = []
const reportedFeatureFlags = new Set()

export default class extends Controller {
  static values = {
    apiKey: String,
    apiHost: String,
    featureFlags: Object
  }
  initialize() {
    this._posthog = this.startPosthog.bind(this);
    this._captureTrialLead = this.captureTrialLead.bind(this)
    this._loading = false;
    this._capturing = false;
    this._posthogInstance = null;
    this._connected = false;
  }
  connect() {
    this._connected = true;
    window.addEventListener("vanilla-cookieconsent--changed", this._posthog)
    window.addEventListener("lead-capture:trial-lead", this._captureTrialLead)
    // Also initialize if cookies were already accepted in a previous session
    this.startPosthog()
  }
  disconnect() {
    this._connected = false;
    window.removeEventListener("vanilla-cookieconsent--changed", this._posthog)
    window.removeEventListener("lead-capture:trial-lead", this._captureTrialLead)
  }
  captureTrialLead(event) {
    if (!initCookieConsent().allowedCategory('analytics')) return

    if (this.posthog) {
      this.posthog.capture("trial_lead_captured", event.detail)
    } else {
      pendingTrialLeads.push(event.detail)
    }
  }
  startPosthog() {
    if (!this._analyticsAllowed()) {
      if (this._capturing) {
        this._posthogInstance.stopSessionRecording()
        this._posthogInstance.opt_out_capturing()
        this._capturing = false
      }
      this.discardCaptureQueue()
      return
    }

    // Prevent double initialization
    if (this._posthogInstance) {
      if (!this._capturing) this._enableCapture(this._posthogInstance)
      return
    }
    if (this._loading) return

    this.startCaptureQueue()
    this._loading = true

    import('posthog-js').then(
      ({ default: posthog }) => {
        this._loading = false
        if (this._connected && this._analyticsAllowed()) this.setupPosthog(posthog)
        else this.discardCaptureQueue()
      },
      () => {
        this._loading = false
        this.discardCaptureQueue()
      }
    )
  }

  _analyticsAllowed() {
    const cc = initCookieConsent()
    return Boolean(this.apiKeyValue && cc.allowedCategory('analytics'))
  }

  startCaptureQueue() {
    this._queuedCaptures = []
    this._captureQueue = { capture: (...args) => this._queuedCaptures.push(args) }
    window.posthog = this._captureQueue
  }

  flushCaptureQueue(posthog) {
    this._queuedCaptures.forEach((args) => posthog.capture(...args))
    this._queuedCaptures = []
  }

  discardCaptureQueue() {
    if (window.posthog === this._captureQueue) delete window.posthog
    this._queuedCaptures = []
  }

  setupPosthog(posthog) {
    this._posthogInstance = posthog
    posthog.init(this.apiKeyValue, {
      api_host: this.apiHostValue,
      ui_host: 'https://eu.posthog.com',
      opt_out_capturing_by_default: true,
      opt_out_persistence_by_default: true,
      // Disable auto-start to manually control recording based on login state
      disable_session_recording: true,
      before_send: truncateExceptionMessages
    })
    this.posthog = posthog
    window.posthog = posthog
    this.flushCaptureQueue(posthog)
    pendingTrialLeads.splice(0).forEach(lead => posthog.capture("trial_lead_captured", lead))

    this._enableCapture(posthog)
  }

  _enableCapture(posthog) {
    this._capturing = true
    posthog.opt_in_capturing({ captureEventName: false })
    const isLoggedIn = window.userServices.id.length > 0;

    if (isLoggedIn) {
      posthog.identify(
        window.userServices.id,
        {
          email: window.userServices.email,
          name: window.userServices.name,
          role: window.userServices.role,
          locale: window.userServices.locale,
          isMetaAdmin: window.userServices.isMetaAdmin,
          $created_at: window.userServices.createdAt
        }
      )
      posthog.group('company', 'id: ' + window.userServices.companyId, {
        name: window.userServices.company,
        plan: window.userServices.plan,
        subscribed: window.userServices.subscribed,
        organizationKind: window.userServices.organizationKind,
        $created_at: window.userServices.companyCreatedAt
      })
      // Override sampling control: always record (ignore dashboard 10% sampling)
      // { sampling: true } = override/ignore sampling, { sampling: false } = respect sampling
      // https://posthog.com/docs/libraries/js/usage#session-replay
      posthog.startSessionRecording({ sampling: true })
    } else {
      // Respect dashboard sampling: 10% of logged-out sessions recorded
      posthog.startSessionRecording()

      // Check if the URL includes the parameter session_logout to reset the session
      const url = new URL(window.location.href)
      if (url.searchParams.get('session_logout') !== null) {
        posthog.reset()
      }
    }

    this.reportFeatureFlags(posthog)

    const registration = this.element.querySelector("[data-registration-user-id]")
    if (registration) {
      if (!isLoggedIn) posthog.identify(registration.dataset.registrationUserId)
      posthog.capture("user_registered", { organization_kind: registration.dataset.registrationOrganizationKind })
      registration.removeAttribute("data-registration-user-id")
    }
  }

  reportFeatureFlags(posthog) {
    const flags = Object.entries(this.featureFlagsValue)
    if (flags.length === 0) return

    posthog.register(Object.fromEntries(flags.map(([key, enabled]) => [`$feature/${key}`, enabled])))
    flags.forEach(([key, enabled]) => {
      const reported = `${key}:${enabled}`
      if (reportedFeatureFlags.has(reported)) return

      reportedFeatureFlags.add(reported)
      posthog.capture("$feature_flag_called", { $feature_flag: key, $feature_flag_response: enabled })
    })
  }
}
